/**
 * Environment-specific default feature flag values
 * These are used when no environment-specific override exists
 */
const DEFAULT_FLAGS = {
    enableSettingsApp: false,
    enableWorkflowsApp: false,
    enableImpersonation: false,
    enableReferralsApp: false,
    enableLinkedinConnectorApp: false,
    enableDarkMode: false,
    enablePromotionsApp: false,
    enableAtsApp: false,
    enableLegacyWebpartsSpike: false,
};
/**
 * Feature flag defaults for local and development environments
 * Dark mode is enabled in these environments for easier testing
 */
const LOCAL_DEV_FLAGS = {
    enableDarkMode: true,
};
/**
 * Feature flag defaults for staging environment
 */
const STAGING_FLAGS = {
    enableDarkMode: true,
};
/**
 * Parse feature flags from environment variables
 * Auto-discovers all FEATURE_* variables injected by webpack DefinePlugin
 * Note: Direct property access is required for webpack DefinePlugin to work
 */
function parseFeatureFlagsFromEnv() {
    const flags = {};
    const enableSettingsApp = process.env.FEATURE_ENABLE_SETTINGS_APP;
    const enableWorkflowsApp = process.env.FEATURE_ENABLE_WORKFLOWS_APP;
    const enableImpersonation = process.env.FEATURE_ENABLE_IMPERSONATION;
    const enableReferralsApp = process.env.FEATURE_ENABLE_REFERRALS_APP;
    const enableLinkedinConnectorApp = process.env.FEATURE_ENABLE_LINKEDIN_CONNECTOR_APP;
    const enableDarkMode = process.env.FEATURE_ENABLE_DARK_MODE;
    const enablePromotionsApp = process.env.FEATURE_ENABLE_PROMOTIONS_APP;
    const enableAtsApp = process.env.FEATURE_ENABLE_ATS_APP;
    const enableLegacyWebpartsSpike = process.env.FEATURE_ENABLE_LEGACY_WEBPARTS_SPIKE;
    if (enableSettingsApp !== undefined) {
        flags.enableSettingsApp = enableSettingsApp === 'true' || enableSettingsApp === '1';
    }
    if (enableWorkflowsApp !== undefined) {
        flags.enableWorkflowsApp = enableWorkflowsApp === 'true' || enableWorkflowsApp === '1';
    }
    if (enableImpersonation !== undefined) {
        flags.enableImpersonation = enableImpersonation === 'true' || enableImpersonation === '1';
    }
    if (enableReferralsApp !== undefined) {
        flags.enableReferralsApp = enableReferralsApp === 'true' || enableReferralsApp === '1';
    }
    if (enableLinkedinConnectorApp !== undefined) {
        flags.enableLinkedinConnectorApp = enableLinkedinConnectorApp === 'true' || enableLinkedinConnectorApp === '1';
    }
    if (enableDarkMode !== undefined) {
        flags.enableDarkMode = enableDarkMode === 'true' || enableDarkMode === '1';
    }
    if (enablePromotionsApp !== undefined) {
        flags.enablePromotionsApp = enablePromotionsApp === 'true' || enablePromotionsApp === '1';
    }
    if (enableAtsApp !== undefined) {
        flags.enableAtsApp = enableAtsApp === 'true' || enableAtsApp === '1';
    }
    if (enableLegacyWebpartsSpike !== undefined) {
        flags.enableLegacyWebpartsSpike = enableLegacyWebpartsSpike === 'true' || enableLegacyWebpartsSpike === '1';
    }
    return flags;
}
/**
 * localStorage key the E2E harness writes flag overrides to. Kept internal —
 * the Playwright helper (`overrideFeatureFlags`) mirrors this literal with a
 * comment rather than importing it, so the test harness has no build-time
 * dependency on this package.
 */
const E2E_FLAG_OVERRIDE_KEY = 'e2e:featureFlags';
/**
 * E2E-only feature-flag override channel.
 *
 * Tier-1 Playwright builds bake every flag ON via `.env.test`, so the
 * flag-*off* behaviour (nav item absent, route unregistered, direct
 * navigation resolving to Page Not Found) had no way to be exercised — there
 * was no flag-off build lane and no runtime override. A spec can now set
 * `localStorage['e2e:featureFlags'] = JSON.stringify({ enablePromotionsApp: false })`
 * (via `page.addInitScript`, before the app boots) to drive a flag-off variant
 * per test without a second CI build.
 *
 * The channel is gated on `FEATURE_E2E_TEST_MODE`, which the bundler inlines as
 * a string literal via `source.define` (same mechanism `TestAuthProvider` and
 * `App.tsx` already rely on). In production that literal is not `'true'`, so
 * the whole branch is dead code the minifier drops — the localStorage read
 * never reaches the production bundle.
 */
function parseE2eFlagOverrides() {
    if (process.env.FEATURE_E2E_TEST_MODE !== 'true') {
        return {};
    }
    try {
        const raw = globalThis.localStorage?.getItem(E2E_FLAG_OVERRIDE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw);
        // Only accept a plain object of flag → boolean; ignore anything else so a
        // malformed override can't crash the app during boot.
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    }
    catch {
        // No localStorage (non-browser context) or malformed JSON — ignore silently.
        return {};
    }
}
/**
 * Get the current environment
 */
function getCurrentEnvironment() {
    // process.env.NODE_ENV is replaced at compile time by webpack's mode option via DefinePlugin
    const nodeEnv = process.env.NODE_ENV;
    if (nodeEnv === 'production') {
        return 'production';
    }
    if (nodeEnv === 'development') {
        return 'development';
    }
    return 'local';
}
/**
 * Get environment-specific defaults
 */
function getEnvironmentDefaults(env) {
    switch (env) {
        case 'local':
            return LOCAL_DEV_FLAGS;
        case 'development':
            return STAGING_FLAGS; // Staging uses development env
        default:
            return {};
    }
}
/**
 * Load feature flags configuration
 * Merges default flags with environment-specific defaults and environment variables
 */
export function loadFeatureFlags() {
    const env = getCurrentEnvironment();
    const envDefaults = getEnvironmentDefaults(env);
    const envFlags = parseFeatureFlagsFromEnv();
    return {
        ...DEFAULT_FLAGS,
        ...envDefaults,
        ...envFlags,
        // E2E override wins over the compile-time env flags so flag-off variants can
        // be driven per test. No-op (returns {}) outside E2E test-mode builds.
        ...parseE2eFlagOverrides(),
    };
}
/**
 * Get the value of a specific feature flag
 */
export function isFeatureEnabled(flags, flagName) {
    return flags[flagName];
}
/**
 * Get current environment name
 */
export function getEnvironment() {
    return getCurrentEnvironment();
}
